import { AxisBottom, AxisLeft } from "@visx/axis";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { scaleLinear, scaleTime } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

interface TrafficChartProps {
	data: Array<{ date: string; sessions: number }>;
}

const margin = { top: 20, right: 30, bottom: 50, left: 50 };

function parseDate(dateStr: string): Date {
	// GA4 date format: YYYYMMDD
	const y = dateStr.slice(0, 4);
	const m = dateStr.slice(4, 6);
	const d = dateStr.slice(6, 8);
	return new Date(`${y}-${m}-${d}`);
}

function formatDate(d: Date): string {
	const month = d.getMonth() + 1;
	const day = d.getDate();
	return `${month}/${day}`;
}

export const TrafficChart = ({ data }: TrafficChartProps) => {
	const containerRef = useRef<HTMLDivElement>(
		null,
	) as React.RefObject<HTMLDivElement>;

	const { width = 0, height = 0 } = useResizeObserver({
		ref: containerRef,
	});

	if (!data.length) {
		return (
			<div>
				<div>No traffic data available for this date range.</div>
				<div className="text-muted">
					Google Analytics is connected successfully. Data will appear once
					traffic is recorded.
				</div>
			</div>
		);
	}

	const points = data.map((d) => ({
		x: parseDate(d.date),
		y: d.sessions,
	}));

	const xScale = scaleTime({
		domain: [
			Math.min(...points.map((p) => p.x.getTime())),
			Math.max(...points.map((p) => p.x.getTime())),
		],
		range: [margin.left, width - margin.right],
	});

	const yScale = scaleLinear<number>({
		domain: [0, Math.max(...points.map((p) => p.y))],
		range: [height - margin.bottom, margin.top],
		nice: true,
	});

	return (
		<div ref={containerRef} style={{ width: "100%", height: "300px" }}>
			<svg width={width} height={height}>
				<title>Website traffic</title>
				<Group>
					<LinePath
						data={points}
						x={(d) => xScale(d.x)}
						y={(d) => yScale(d.y)}
						curve={curveMonotoneX}
						stroke="#0d6efd"
						strokeWidth={2}
					/>
					<AxisBottom
						top={height - margin.bottom}
						scale={xScale}
						stroke="#333"
						tickStroke="#333"
						tickFormat={(d) => formatDate(d as Date)}
						numTicks={Math.min(points.length, 8)}
					/>
					<AxisLeft
						left={margin.left}
						scale={yScale}
						stroke="#333"
						tickStroke="#333"
						tickFormat={(d: { valueOf(): number }) =>
							Math.floor(d.valueOf()).toString()
						}
					/>
				</Group>
			</svg>
		</div>
	);
};
